import { FieldValues, useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { lt, FormRadioGroupField, useLocalizeCurrency } from '@mews/framework';
import { Container, RadioButton, Stack, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { UncontrolledNumberInputField } from '@mews-commander/form';
import {
    AdjustmentType,
    ConfigureProductFormStep,
    getCurrentServiceFieldName,
    getServicesPropertiesFieldName,
    PricingFieldNames,
    ServicesFieldNames,
} from '../../../forms/schemas/configureProductFormSchema';
import { useEnterprisesProductContext } from '../../../../context/EnterprisesProductContext';
import { PriceInput } from '../../common/PriceInput';
import { AddProductToServiceFormContentProps } from '../../../LocalProductSidePanel/forms/AddProductToServiceForm/types';
import { PercentageAdjustmentSelect } from './PercentagePricing/PercentageAdjustmentSelect';

const adjustmentOptions = [
    { value: AdjustmentType.None, label: lt('ConfigureProductServicesNoAdjustment') },
    { value: AdjustmentType.FixedPriceAdjustments, label: lt('GeneralFixedPrice') },
    { value: AdjustmentType.PercentagePriceAdjustments, label: lt('GeneralPercentage') },
];

interface Props extends Pick<AddProductToServiceFormContentProps, 'basePriceValue'> {
    currentService: string;
}
export const ServicesPricingSingleProperty = ({ currentService, basePriceValue }: Props) => {
    const {
        state: { enterprises },
    } = useEnterprisesProductContext();
    const { id } = enterprises[0] || {};

    const { getValues, getFieldState, setValue, watch, unregister, register } = useFormContext<FieldValues>();
    const localizeCurrency = useLocalizeCurrency();

    const adjustmentFieldName = `${getCurrentServiceFieldName(currentService)}.${
        ServicesFieldNames.PriceAdjustmentType
    }`;
    const currentPriceAdjustmentType = watch(adjustmentFieldName);

    const priceFieldName = getServicesPropertiesFieldName(currentService, currentPriceAdjustmentType, id);
    const { isDirty } = getFieldState(priceFieldName);

    // information from previous steps of the form
    // for enterprise product "add to service" the base price is directly from the enterprise
    const centralProductBasePrice = { price: basePriceValue?.netValue, currency: basePriceValue?.currency };
    const currentBasePrice = getValues(ConfigureProductFormStep.Pricing)?.[id] || centralProductBasePrice;
    const defaultCurrencyCode =
        getValues(`${ConfigureProductFormStep.Pricing}.${id}.${PricingFieldNames.CurrencyCode}`) ||
        centralProductBasePrice.currency;

    useEffect(() => {
        if (currentPriceAdjustmentType === AdjustmentType.FixedPriceAdjustments) {
            unregister(getServicesPropertiesFieldName(currentService, AdjustmentType.PercentagePriceAdjustments, id));
            setValue(priceFieldName, { [PricingFieldNames.CurrencyCode]: defaultCurrencyCode });
        } else {
            unregister(getServicesPropertiesFieldName(currentService, AdjustmentType.FixedPriceAdjustments, id));
        }
    }, [
        currentPriceAdjustmentType,
        currentService,
        defaultCurrencyCode,
        id,
        priceFieldName,
        register,
        setValue,
        unregister,
    ]);

    return (
        <>
            <FormRadioGroupField
                name={adjustmentFieldName}
                label={lt('ConfigureProductServicesFieldAdjustmentType')}
                defaultValue={AdjustmentType.None}
            >
                {adjustmentOptions.map(({ value, label }) => (
                    <RadioButton label={label} value={value} key={value} />
                ))}
            </FormRadioGroupField>
            <Stack vertical>
                {currentPriceAdjustmentType === AdjustmentType.FixedPriceAdjustments && (
                    <PriceInput
                        label={lt('GeneralAdjustedPriceTaxExcl')}
                        defaultCurrencyCode={defaultCurrencyCode}
                        isDirty={isDirty}
                        name={`${priceFieldName}.${ServicesFieldNames.Value}`}
                        size="small"
                    />
                )}
                {currentPriceAdjustmentType === AdjustmentType.PercentagePriceAdjustments && (
                    <Stack spacing="50" distribution="stretch">
                        <UncontrolledNumberInputField
                            name={`${priceFieldName}.${ServicesFieldNames.Multiplier}`}
                            label={lt('GeneralAdjustedPriceTaxExcl')}
                            required
                            suffix={UnicodeEntity.Percent}
                            type="number"
                        />
                        <PercentageAdjustmentSelect name={`${priceFieldName}.${ServicesFieldNames.AmountPart}`} />
                    </Stack>
                )}
                {[AdjustmentType.FixedPriceAdjustments, AdjustmentType.PercentagePriceAdjustments].includes(
                    currentPriceAdjustmentType
                ) && (
                    <Container padding={['200', '0', '0', '0']}>
                        <Typography textStyle="bodySmall" variant="tertiary" as="div">
                            {lt('GeneralPriceExcludingTax')}
                            {UnicodeEntity.Colon}
                            {UnicodeEntity.Space}
                            {localizeCurrency(currentBasePrice.price, defaultCurrencyCode)}
                        </Typography>
                    </Container>
                )}
            </Stack>
        </>
    );
};
